

import {ProjectElement} from './project.element';
import {ProjectElementType} from './project.element.type';
import {Project} from '../project';

import {Logger} from '@/util/logger';

import {basename, empty} from '@/util/util';

import {first} from 'rxjs/operators';
import {forkJoin} from 'rxjs';
import {ProjectElementFactoryDecorator} from "@/models/project/project.element.factory.decorator";

import {PropertiesHolder} from "@/models/properties.holder";
import {BlobType} from "@/models/blob";
import {JSONBrowserMetadata} from "@/screens/ide/json.browser.metadata";
import {RegressionViewerMetadata} from "@/screens/ide/regression.viewer.metadata";

/**
 *
 * @class RegressionElement
 *
 * node to maps to blob of JSON data that collections up all the regression data
 * for a corridor batch.  Basically the "regression" for a corridor.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2022
 *
 */
@ProjectElementFactoryDecorator('RegressionElement')
export class RegressionElement extends ProjectElement {

  protected actionKeys: string[] = [
    'downloadregression',
    'downloadregressioncsv',
    'downloadregressionjson',
    'viewregression',
    'renameregression',
    'deleteregression',

  ];

  /** blob key for the regression data */
  public _blob: string = '';

  /** start date bracket */
  public _start: string = '';

  /** end date bracket */
  public _end: string = '';

  /** the corridor */
  public _corridor: string = '';

  /** standard constructor */
  constructor(
    project:     Project,
    id:          string,
    regression:  string,
    start:       string,
    end:         string,
    corridor:    string,
    parent:      ProjectElement,
    publish?:    boolean,
    peType?:     ProjectElementType
  ) {

    super(project, id, peType ? peType : ProjectElementType.RegressionResult);

    this._blob        = regression;
    this._start       = start;
    this._end         = end;
    this._corridor    = corridor;

    this._label       = 'Regression ' + start + '_' + end;
    this._icon        = 'fa fa-chart-line';
    this._elementKind = 'RegressionElement';

    if (parent) {

      this._project.addElement(this, parent, publish);
    }

  }

  /** helper to use to for static global constructors (and factory registration) */
  static requireType() {
    console.log("requiring RegressionElement");
  }

  /** drop data on this element */
  public acceptDrop(data): boolean {

    return false;
  }

  /** is this element deletable? */
  public isDeletable(): boolean {

    const kids: ProjectElement[] = this.children();

    return kids.length <= 0;
  }

  /** test if any blob of this type can be a child of this element */
  public acceptsBlobType(type: BlobType): boolean {

    return false;
  }

  /** convert this element to a simplified form that can be easily serialized */
  public node(init?: any) {

    return super.node({
      blob:     this._blob,
      start:    this._start,
      end:      this._end,
      corridor: this._corridor
    });
  }

  /** initialize this element given the data in the simplified node object */
  public fromNode(node: any) {

    /* recover the basics */

    super.fromNode(node);

    /* recover the job details */

    this._blob     = node.blob;
    this._start    = node.start;
    this._end      = node.end;
    this._corridor = node.corridor;
  }

  /*
   * when blobs get moved/copied in bulk, project elements have to allow being told where
   * their blobs have been moved to.
   *
   */
  public adjusted(searchKey: string, replaceKey: string) {

    if (this._blob === searchKey) {

      this._blob = replaceKey;
    }

    super.adjusted(searchKey, replaceKey);
  }

  /** fetch any secondary blobs for this element */
  public secondaryBlobs(): string[] {

    return [this._blob];
  }

  /** do we actually have a regression? */
  public hasRegression(): boolean {

    return !empty(this._blob);
  }

  /**
   * subclasses must add their menu item definition(s) to the given action set,
   * in jquery-contextmenu fashion.
   *
   * @param actionSet any
   *
   */
  public actions(actionSet: any): any {

    actionSet.downloadregression = {
      name: 'View',
      icon: 'fas fa-chart-line',
      disabled: !this.hasRegression()
    };

    actionSet.downloadregressioncsv = {
      name: 'Download (CSV)',
      icon: 'fas fa-file-csv',
      disabled: !this.hasRegression()
    };

    actionSet.downloadregressionjson = {
      name: 'Download (JSON)',
      icon: 'fas fa-file-download',
      disabled: !this.hasRegression()
    };

    actionSet.viewregression = {
      name: 'View (JSON)',
      icon: 'fas fa-keyboard',
      disabled: !this.hasRegression()
    };

    actionSet.renameregression = {
      name: 'Rename',
      icon: 'fas fa-tag',
      disabled: false
    };

    actionSet.deleteregression = {
      name: 'Delete',
      icon: 'fas fa-trash',
      disabled:  false
    };

    return actionSet;
  }

  /** test if this element accepts the given action */
  public acceptsAction(key: string): boolean {

    return !!this.actionKeys.find(x => x === key);
  }

  /** append these elements properties to the holder */
  public properties(properties?: PropertiesHolder): PropertiesHolder {

    if (!properties) {

      properties = new PropertiesHolder();

    }

    properties.set('Start',      this._start);
    properties.set('End',        this._end);
    properties.set('Corridor',   this._corridor);
    properties.set('Regression', this._blob);

    /* add in the usual properties */

    return super.properties(properties);
  }

  /** helper to delete any cached artifacts from the remote simulation */
  public deleteResult(selection: ProjectElement[]) {

    if (this.pendingKeys.length > 0) {

      /* busy right now */

      return ;
    }

    /*
     * the cached results are internal to a "result", we just need to batch them up,
     * and remove them all with a fork/join.  In our case there are just two artifacts,
     * the log and the output.
     *
     */

    this.pendingKeys = [];

    this.pendingKeys.push([this._blob, 'latest']);

    /* build out the blob deletion observables ... */

    Logger.debug('[regression] deleting blobs: ', this.pendingKeys);

    /* create the observables for copying blobs... */

    const observables: any[] = [];

    for (const f of this.pendingKeys) {

      /* as we copy we are also forcing the type to be whatever the user selected as the import type */

      observables.push( this._project.services.blobber.remove(f[0], f[1]).pipe(first()) );
    }

    /* now fork & join to do them all in parallel */

    forkJoin(observables).subscribe(
      (results) => {

        /* everything got deleted ok, add the children in the project and save the latest project */

        Logger.debug('[regression] blobs deleted: ', results);

        /* make sure the project is saved */

        this.pendingKeys = [];

        /* delete self */

        this.deleteElements([this]);

      },
      (error) => {

        Logger.error('[regression] problems deleting blobs: ', error);

      });

  }

  /** subclasses must implement their own actions */
  public action(key: string, selection: ProjectElement[]) {

    if ( !this.actionKeys.find(x => x === key) ) {

      /* let ProjectElement handle it */

      this._parent.action(key, selection);

      return ;
    }

    switch (key) {

      case 'downloadregression': {

        this._project.services.ide.launchWindow(
          new RegressionViewerMetadata(
            '',
            '',
            this._blob,
            this._label
          )
        );

        break;
      }

      case 'downloadregressioncsv': {

        this.downloadCSV();

        break;
      }

      case 'downloadregressionjson': {

        this.dowloadBlob(BlobType.Json, this._blob, 'latest', basename(this._blob));
        break;
      }

      case 'viewregression': {

        this._project.services.ide.launchWindow(
          new JSONBrowserMetadata(
            '',
            '',
            this._blob
          )
        );

        break;
      }

      case 'renameregression': {

        const re: RegressionElement = this;

        re._project.services.modals.open(
          'rename-element-modal',
          (name, resolve, reject) => {

            name = name.trim();

            Logger.debug('[RegressionElement] validating: ', name);

            /* make sure we aren't name clashing ...  */

            const peers: ProjectElement[] = re._parent._children;

            for (const peer of peers) {

              if (peer._label === name) {

                reject('Regression of that name already exists.');

                return ;
              }

            }

            resolve(name);

          },
          (name) => {

            /* new label was accepted */

            Logger.debug('[RegressionElement] renaming: ', name);

            re._label = `${name.trim()}`;

            re.redraw();

            re.save();
          },
          {
            title: 'Rename Regression',
            placeholder: this._label
          });

        }
        break;

      case 'deleteregression': {

        Logger.debug('[RegressionElement] deleting');

        this.deleteElements(selection);

        break;
      }

    }
  }

  /** helper for format the regression data as a CSV...and then download that. */
  downloadCSV() {

    /* fetch the data ... */

    this._project.services.blobber.contents(this._blob, 'latest', 'json')
      .pipe(first())
      .subscribe(
        (content: any) => {

          /* set the meta data */

          Logger.debug(`[RegressionElement] creating CSV: `, content);

          const corridor: string              = content.user_params.corridor;
          const start: string                 = content.user_params.end_date;
          const end: string                   = content.user_params.start_date;
          const series: Map<string, number[]> = new Map<string, number[]>();

          /*
           * each of the data series is encoded as an object; the property names are
           * just the ordering index.
           *
           */

          /* read off the data series... */

          Object.entries(content.historic_data).forEach(([key, value]) => {

            const seriesName: string = key;

            let values: {idx: number, value: number}[] = [];

            Object.entries(value).forEach(([tag, datum]) => {

              const   idx: number = parseInt(tag);
              const value: number = datum as number;

              values.push({idx, value});
            });

            /* ensure its properly sorted */

            values.sort((a: {idx: number, value: number}, b: {idx: number, value: number}) => {

              return a.idx - b.idx;

            });

            /* remember this series */

            series.set(seriesName, values.map(x => x.value));

          });

          if (series.size == 0) {

            /* no data? */

            Logger.error(`[RegressionElement] no data series?`);

            return ;
          }

          /* the columns of the table are the series, and the rows are the series data (within each column) */

          /* all data series must have at least the same number of data points */

          const N: number = Array.from(series.values())[0].length;

          for (let [k, v] of series) {

            if (v.length < N) {

              Logger.error(`[RegressionElement] the '${k}' series does not have enough columns ${v.length} < ${N}.`);
            }

          }

          /* build the CSV data... */

          const lines: string[] = [];

          /* the header */

          lines.push(Array.from(series.keys()).join(','));

          /* the rows */

          for (let i=0; i<N; i++) {

            lines.push(Array.from(series.keys()).map(k => series.get(k)[i]).join(','));

          }

          const text: string = lines.join("\n");
          let name: string = basename(this._label) + ".csv";

          Logger.debug(`[RegressionElement] csv created.`);

          /* now download *that* to the user */

          let blob: Blob = new Blob([text], {type: 'text/csv'});

          const url  = window.URL.createObjectURL(blob);

          /* setup a fake one-time link with the right download name and the blob attached as the download */

          const element: HTMLAnchorElement = document.createElement('a');
          element.href          = url;
          element.download      = name || 'download';

          element.style.display = 'none';

          document.body.appendChild(element);

          /* do it */

          element.click();

          /* clean up */

          document.body.removeChild(element);

        },
        (error) => {

          Logger.error(`[RegressionElement] could not fetch regression data: `, error);
        });
  }

}
